import { useEffect, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Check, X, FileSpreadsheet, FileText, Loader2, Tag, BarChart3 } from "lucide-react";
import { V } from "@/lib/vocab";

const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : ""; } catch { return ""; } };
const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Manage modal: custom Categories (CRUD) + Reports generator (CSV + PDF).
export function RequestsManageModal({ open, onOpenChange, tenantId, builtIns, customCats, onCatsChanged, tenantName, readOnly }) {
  const [tab, setTab] = useState("categories");
  const [newLabel, setNewLabel] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [editing, setEditing] = useState(null); // {id, label, description}
  const [savingCat, setSavingCat] = useState(false);

  // Reports
  const [rStatus, setRStatus] = useState("");
  const [rCategory, setRCategory] = useState("");
  const [rCreatedBy, setRCreatedBy] = useState("");
  const [rFrom, setRFrom] = useState("");
  const [rTo, setRTo] = useState("");
  const [groupBy, setGroupBy] = useState("category");
  const [includeConv, setIncludeConv] = useState(false);
  const [report, setReport] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => { if (!open) { setEditing(null); setNewLabel(""); setNewDesc(""); } }, [open]);

  // Who a board request is pre-addressed to.
  const [managers, setManagers] = useState([]);
  const [primary, setPrimary] = useState("");
  const [savingPrimary, setSavingPrimary] = useState(false);
  useEffect(() => {
    if (!open || !tenantId) return;
    api.get(`/tenants/${tenantId}/community-managers`)
      .then((r) => { setManagers(r.data.managers || []); setPrimary(r.data.primary_manager_id || ""); })
      .catch(() => setManagers([]));
  }, [open, tenantId]);
  const savePrimary = async (uid) => {
    setSavingPrimary(true);
    const before = primary;
    setPrimary(uid);
    try {
      await api.put(`/tenants/${tenantId}/primary-manager`, { user_id: uid });
      toast.success(uid ? `${V.Grp()} requests will be pre-addressed to them` : `${V.many("board")} will pick each time`);
    } catch (e) { setPrimary(before); toast.error(e.response?.data?.detail || "Couldn't save that"); }
    setSavingPrimary(false);
  };

  const catCode = (v) => (builtIns.find((b) => b[0] === v)?.[1]) || (customCats.find((c) => c.code === v)?.label) || titleCase(v);

  const addCat = async () => {
    if (!newLabel.trim()) return;
    setSavingCat(true);
    try { await api.post(`/tenants/${tenantId}/request-categories`, { label: newLabel.trim(), description: newDesc.trim() }); setNewLabel(""); setNewDesc(""); onCatsChanged(); toast.success("Category added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
    finally { setSavingCat(false); }
  };
  const saveEdit = async () => {
    if (!editing?.label.trim()) return;
    try { await api.put(`/tenants/${tenantId}/request-categories/${editing.id}`, { label: editing.label.trim(), description: (editing.description || "").trim() }); setEditing(null); onCatsChanged(); toast.success("Saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const delCat = async (id) => {
    try { await api.delete(`/tenants/${tenantId}/request-categories/${id}`); onCatsChanged(); toast.success("Category removed"); }
    catch (e) { toast.error("Could not remove"); }
  };

  const generate = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.get(`/tenants/${tenantId}/actions/report`, { params: { status: rStatus, category: rCategory, created_by: rCreatedBy, start: rFrom, end: rTo, group_by: groupBy, include_conversations: includeConv ? "1" : "" } });
      setReport(data);
    } catch { toast.error("Could not generate report"); }
    finally { setGenLoading(false); }
  };

  const rangeLabel = () => (rFrom || rTo) ? `${rFrom ? fmtDate(rFrom) : "start"} – ${rTo ? fmtDate(rTo) : "today"}` : "All time";

  const fmtDateTime = (iso) => { try { return iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""; } catch { return ""; } };

  const exportCsv = () => {
    if (!report) return;
    const withConv = includeConv && report.rows.some((r) => (r.conversation || []).length);
    const headers = ["Ref", "Title", "Status", "Category", "Priority", "Created by", "Assigned to", "Unit", "Created", "Description", ...(withConv ? ["Conversation"] : [])];
    const rows = report.rows.map((r) => [
      r.ref || "", r.title || r.summary || "", r.status || "", catCode(r.category || r.type || ""), r.priority || "",
      r.created_by || r.resident || "", r.assignee_name || "", r.unit || "", fmtDate(r.created_at), r.details || "",
      ...(withConv ? [(r.conversation || []).map((m) => `${m.who} (${fmtDateTime(m.at)}): ${m.body}`).join("\n")] : []),
    ]);
    const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = `requests-report-${Date.now()}.csv`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  const exportPdf = () => {
    if (!report) return;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const M = 48; let y = M;
    const RED = [229, 72, 77], INK = [28, 25, 23], GRAY = [120, 113, 108], LINE = [231, 226, 217];
    doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(...RED); doc.text("REQUESTS REPORT", M, y); y += 20;
    doc.setFontSize(20); doc.setTextColor(...INK); doc.text(tenantName || "Community", M, y); y += 18;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10.5); doc.setTextColor(...GRAY);
    doc.text(`${rangeLabel()}  ·  ${report.total} request${report.total === 1 ? "" : "s"}  ·  grouped by ${titleCase(report.group_by)}`, M, y); y += 14;
    const filt = [rStatus && `Status: ${titleCase(rStatus)}`, rCategory && `Category: ${catCode(rCategory)}`, rCreatedBy && `Created by: ${rCreatedBy}`].filter(Boolean).join("   ");
    if (filt) { doc.text(filt, M, y); y += 14; }
    doc.setDrawColor(...INK); doc.setLineWidth(1.2); doc.line(M, y, doc.internal.pageSize.getWidth() - M, y); y += 18;
    autoTable(doc, { startY: y, margin: { left: M, right: M }, head: [[`By ${titleCase(report.group_by)}`, "Count"]],
      body: report.summary.map((s) => [titleCase(catCode(s.key)), String(s.count)]),
      styles: { fontSize: 10, cellPadding: 6, textColor: INK, lineColor: LINE, lineWidth: 0.5 },
      headStyles: { fillColor: RED, textColor: [255, 255, 255], fontStyle: "bold" }, alternateRowStyles: { fillColor: [250, 249, 247] },
      columnStyles: { 1: { halign: "right", cellWidth: 80, fontStyle: "bold" } }, theme: "grid" });
    y = doc.lastAutoTable.finalY + 20;
    autoTable(doc, { startY: y, margin: { left: M, right: M },
      head: [["Ref", "Title", "Status", "Category", "Created by", "Assigned to", "Unit", "Created"]],
      body: report.rows.map((r) => [r.ref || "", r.title || r.summary || "", titleCase(r.status || ""), titleCase(catCode(r.category || r.type || "")), r.created_by || r.resident || "", r.assignee_name || "—", r.unit || "—", fmtDate(r.created_at)]),
      styles: { fontSize: 8, cellPadding: 4, textColor: INK, lineColor: LINE, lineWidth: 0.4, overflow: "linebreak" },
      headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 }, alternateRowStyles: { fillColor: [250, 249, 247] },
      columnStyles: { 0: { cellWidth: 52 }, 1: { cellWidth: 120 }, 6: { cellWidth: 34 } }, theme: "grid" });
    // Conversation transcripts — one section per request, so the report tells the full story
    if (includeConv) {
      const withConv = report.rows.filter((r) => (r.conversation || []).length || (r.details || "").trim());
      if (withConv.length) {
        doc.addPage(); y = M;
        doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(...INK);
        doc.text("Request details & conversations", M, y); y += 10;
        for (const r of withConv) {
          const body = [];
          if ((r.details || "").trim()) body.push(["Description", (r.details || "").trim()]);
          for (const m of r.conversation || []) body.push([`${m.who}\n${fmtDateTime(m.at)}`, m.body]);
          autoTable(doc, { startY: y + 12, margin: { left: M, right: M },
            head: [[`${r.ref || ""}  ·  ${r.title || r.summary || "Request"}`, `${titleCase(r.status || "")}${r.resident ? `  ·  ${r.resident}` : ""}${homeLabel(r) ? `  ·  ${homeLabel(r)}` : ""}`]],
            body,
            styles: { fontSize: 8.5, cellPadding: 5, textColor: INK, lineColor: LINE, lineWidth: 0.4, overflow: "linebreak" },
            headStyles: { fillColor: RED, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
            alternateRowStyles: { fillColor: [250, 249, 247] },
            columnStyles: { 0: { cellWidth: 110, fontStyle: "bold", textColor: GRAY } }, theme: "grid" });
          y = doc.lastAutoTable.finalY + 6;
          if (y > doc.internal.pageSize.getHeight() - 120) { doc.addPage(); y = M - 12; }
        }
      }
    }
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) { doc.setPage(i); doc.setFontSize(8); doc.setTextColor(...GRAY); doc.text(`Ask Redline · Page ${i} of ${pages}`, M, doc.internal.pageSize.getHeight() - 24); }
    doc.save(`${(tenantName || "Community").replace(/[^\w\s-]/g, "")} — Requests Report.pdf`);
    toast.success("PDF downloaded");
  };

  const allCatOptions = [...builtIns, ...customCats.map((c) => [c.code, c.label])];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="req-manage-modal">
        <DialogHeader><DialogTitle>Manage requests</DialogTitle></DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full max-w-xs">
            <TabsTrigger value="categories" data-testid="manage-tab-categories"><Tag className="h-3.5 w-3.5 mr-1.5" /> Categories</TabsTrigger>
            <TabsTrigger value="reports" data-testid="manage-tab-reports"><BarChart3 className="h-3.5 w-3.5 mr-1.5" /> Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="categories" className="pt-4">
            {!readOnly && (
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2">
                  <input data-testid="new-category-input" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()}
                    placeholder="New category name (e.g. Landscaping)" className="flex-1 rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                  <button data-testid="add-category-btn" onClick={addCat} disabled={savingCat || !newLabel.trim()} className="rounded-full bg-redline-500 text-white font-semibold px-4 py-2.5 text-sm flex items-center gap-1.5 hover:bg-redline-600 disabled:opacity-50">
                    {savingCat ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
                  </button>
                </div>
                <input data-testid="new-category-desc" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} maxLength={200}
                  placeholder="When should the AI pick this? (optional — e.g. 'tree trimming, mowing, irrigation issues')" className="w-full rounded-xl border border-sand-200 px-3.5 py-2 text-xs outline-none focus:ring-2 focus:ring-redline-500/30" />
                <p className="text-[11px] text-ink/45">The concierge (text & phone) files {V.lc("person")} requests under these categories automatically — a short hint helps it pick the right one.</p>
              </div>
            )}
            <div className="text-[11px] uppercase tracking-wide font-bold text-ink/40 mb-2">Your categories</div>            {customCats.length === 0 ? <p className="text-sm text-ink/50 mb-4">No custom categories yet. Add one above.</p> : (
              <div className="space-y-1.5 mb-5">
                {customCats.map((c) => (
                  <div key={c.id} data-testid={`category-row-${c.id}`} className="flex items-center gap-2 rounded-xl border border-sand-200 px-3 py-2">
                    {editing?.id === c.id ? (
                      <>
                        <div className="flex-1 space-y-1.5">
                          <input autoFocus value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveEdit()} className="w-full rounded-lg border border-redline-300 px-2.5 py-1.5 text-sm outline-none" />
                          <input data-testid={`category-desc-${c.id}`} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveEdit()} maxLength={200}
                            placeholder="When should the AI pick this? (optional)" className="w-full rounded-lg border border-sand-200 px-2.5 py-1.5 text-xs outline-none" />
                        </div>
                        <button data-testid={`category-save-${c.id}`} onClick={saveEdit} className="h-8 w-8 rounded-lg hover:bg-emerald-50 text-emerald-600 flex items-center justify-center"><Check className="h-4 w-4" /></button>
                        <button onClick={() => setEditing(null)} className="h-8 w-8 rounded-lg hover:bg-sand-100 text-ink/50 flex items-center justify-center"><X className="h-4 w-4" /></button>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 min-w-0">
                          <span className="block text-sm font-medium text-ink">{c.label}</span>
                          {c.description ? <span className="block text-xs text-ink/45 truncate">{c.description}</span> : null}
                        </div>
                        {!readOnly && <>
                          <button data-testid={`category-edit-${c.id}`} onClick={() => setEditing({ id: c.id, label: c.label, description: c.description || "" })} className="h-8 w-8 rounded-lg hover:bg-sand-100 text-ink/50 hover:text-ink flex items-center justify-center"><Pencil className="h-4 w-4" /></button>
                          <button data-testid={`category-delete-${c.id}`} onClick={() => delCat(c.id)} className="h-8 w-8 rounded-lg hover:bg-red-50 text-ink/50 hover:text-danger-600 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
                        </>}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="text-[11px] uppercase tracking-wide font-bold text-ink/40 mb-2">Built-in categories</div>
            <div className="flex flex-wrap gap-1.5">
              {builtIns.map(([code, label]) => <span key={code} className="text-xs font-medium rounded-full border border-sand-200 bg-sand-50 px-2.5 py-1 text-ink/60">{label}</span>)}
            </div>
            {!readOnly && (
              <div className="mt-5 border-t border-sand-100 pt-4" data-testid="board-primary-block">
                <div className="text-[11px] uppercase tracking-wide font-bold text-ink/40 mb-2">{V.Grp()} requests</div>
                <label className="text-sm font-semibold">Primary manager</label>
                <select data-testid="primary-manager-select" value={primary} disabled={savingPrimary}
                  onChange={(e) => savePrimary(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60">
                  <option value="">No default — the {V.grp()} picks each time</option>
                  {managers.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-ink/45">
                  When {V.a("board")} raises a request, this manager is pre-selected. They can pick
                  someone else, and only the person they pick is alerted.
                  {managers.length === 0 ? ` No managers are set up for this ${V.lc("org")} yet.` : ""}
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="reports" className="pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold text-ink/60">From</label><input data-testid="report-from" type="date" value={rFrom} onChange={(e) => setRFrom(e.target.value)} className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none" /></div>
              <div><label className="text-xs font-semibold text-ink/60">To</label><input data-testid="report-to" type="date" value={rTo} onChange={(e) => setRTo(e.target.value)} className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none" /></div>
              <div><label className="text-xs font-semibold text-ink/60">Status</label>
                <select data-testid="report-status" value={rStatus} onChange={(e) => setRStatus(e.target.value)} className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white outline-none">
                  <option value="">All</option><option value="open">Open</option><option value="in_progress">In process</option><option value="on_hold">On hold</option><option value="resolved">Completed</option>
                </select></div>
              <div><label className="text-xs font-semibold text-ink/60">Category</label>
                <select data-testid="report-category" value={rCategory} onChange={(e) => setRCategory(e.target.value)} className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white outline-none">
                  <option value="">All</option>{allCatOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select></div>
              <div><label className="text-xs font-semibold text-ink/60">Created by</label><input data-testid="report-createdby" value={rCreatedBy} onChange={(e) => setRCreatedBy(e.target.value)} placeholder="Any" className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none" /></div>
              <div><label className="text-xs font-semibold text-ink/60">Group by</label>
                <select data-testid="report-groupby" value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white outline-none">
                  <option value="category">Category</option><option value="status">Status</option><option value="priority">Priority</option><option value="assignee_name">Assignee</option><option value="unit">Unit</option>
                </select></div>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm font-medium text-ink/80 cursor-pointer select-none">
              <input type="checkbox" data-testid="report-include-conv" checked={includeConv} onChange={(e) => setIncludeConv(e.target.checked)}
                className="h-4 w-4 rounded border-sand-300 accent-[#e5342e]" />
              Include descriptions &amp; conversation transcripts <span className="text-xs text-ink/45">(adds the full {V.lc("person")} thread to CSV & PDF)</span>
            </label>
            <button data-testid="report-generate" onClick={generate} disabled={genLoading} className="mt-4 rounded-full bg-ink text-white font-semibold px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-ink-700 disabled:opacity-50">
              {genLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4" />} Generate report
            </button>

            {report && (
              <div className="mt-5" data-testid="report-result">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <span className="text-sm font-bold text-ink">{report.total} request{report.total === 1 ? "" : "s"} · {rangeLabel()}</span>
                  <div className="flex items-center gap-2">
                    <button data-testid="report-export-csv" onClick={exportCsv} disabled={!report.total} className="rounded-full border border-sand-200 bg-white px-3.5 py-2 text-sm font-semibold flex items-center gap-1.5 hover:border-ink disabled:opacity-40"><FileSpreadsheet className="h-4 w-4" /> CSV</button>
                    <button data-testid="report-export-pdf" onClick={exportPdf} disabled={!report.total} className="rounded-full bg-redline-500 text-white px-3.5 py-2 text-sm font-semibold flex items-center gap-1.5 hover:bg-redline-600 disabled:opacity-40"><FileText className="h-4 w-4" /> PDF</button>
                  </div>
                </div>
                <div className="rounded-xl border border-sand-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-sand-50 border-b border-sand-200"><tr><th className="text-left px-4 py-2 font-bold text-ink/60">By {titleCase(report.group_by)}</th><th className="text-right px-4 py-2 font-bold text-ink/60">Count</th></tr></thead>
                    <tbody>
                      {report.summary.length === 0 ? <tr><td colSpan={2} className="px-4 py-6 text-center text-ink/50">No requests match those filters.</td></tr> :
                        report.summary.map((s) => <tr key={s.key} className="border-b border-sand-100 last:border-0"><td className="px-4 py-2 text-ink">{titleCase(catCode(s.key))}</td><td className="px-4 py-2 text-right font-bold tabular-nums">{s.count}</td></tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
